import { type FormEvent, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Copy,
  ListChecks,
  Loader2,
  ClipboardPaste,
  Plus,
  Search,
} from 'lucide-react';
import { adminBinderTemplates, type BinderTemplateBlindingScope } from '@/api/client';
import { PageHeader } from '@/components/layout/PageHeader';
import { errorText, errorToneClass } from '@/lib/permissionError';

/**
 * F8b-follow-up (2026-08-06) - dedicated page replacing the previous
 * "New template" modal on BinderTemplatesListPage. The modal was cramped:
 * three init modes + a scrollable master picker + a paste-outline textarea
 * cannot coexist inside a 560-px popup without every affordance feeling
 * squeezed. This page gives each section real breathing room and lets the
 * checkbox picker be a real full-width list.
 */
type Mode = 'clone' | 'checkbox' | 'outline';

// Punch-list f23 — mirrors the backend's SAFE_NAME_RE (_schema.ts) so the
// user sees this rejection immediately instead of after a round trip.
// Control-char range is deliberate, not a typo — eslint-disable justified.
// eslint-disable-next-line no-control-regex
const SAFE_NAME_RE = /^[^\\/\x00-\x1F]+$/;

const MODE_META: Record<
  Mode,
  { title: string; desc: string; icon: typeof Copy }
> = {
  clone: {
    title: 'Clone from an existing template',
    desc: 'Start from Standard (or any other template) and edit after.',
    icon: Copy,
  },
  checkbox: {
    title: 'Pick from the standard folder catalog',
    desc: 'Tick the folders you want; use for straightforward sponsor-specific subsets.',
    icon: ListChecks,
  },
  outline: {
    title: 'Paste an indented folder outline',
    desc: 'For migrations from an existing ISF SOP. Indent with tabs or spaces.',
    icon: ClipboardPaste,
  },
};

/**
 * WHICH BINDER this template builds — not which kind of study it is for.
 *
 * Reported 2026-10-11: "unable to create open label templates as the system
 * mandates the required field blinded/unblinded, no option for open label."
 * There is no gap — an open-label study takes a 'blinded'-scope template as its
 * binder_template_id and simply has no second binder (see
 * handlers/studies/_binderTemplates.ts::resolveStudyBinderTemplates). The
 * labels led with "Blinded"/"Unblinded", so a user setting up an open-label
 * study reasonably concluded neither applied.
 *
 * The stored values are unchanged. blinding_scope is a shared key across
 * templates, study bindings, defaults and validation; renaming it to fix a
 * caption would be a large change for no behavioural gain.
 */
const SCOPE_OPTIONS: Array<{
  value: BinderTemplateBlindingScope;
  label: string;
  hint: string;
}> = [
  {
    value: 'blinded',
    label: 'Study binder',
    hint: 'Protocol, ICF, DoA and document routing. Every study has one — including open-label.',
  },
  {
    value: 'unblinded',
    label: 'Unblinded binder',
    hint: 'IP and randomisation uploads, kept separate from the study binder. Blinded studies only.',
  },
];

export function NewBinderTemplatePage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const cloneFromParam = searchParams.get('clone_from') ?? '';
  const suggestedName = searchParams.get('name') ?? '';

  const [mode, setMode] = useState<Mode>('clone');
  const [name, setName] = useState(suggestedName);
  const [blindingScope, setBlindingScope] = useState<'' | BinderTemplateBlindingScope>('');
  const [description, setDescription] = useState('');
  const [outline, setOutline] = useState('');
  const [cloneFrom, setCloneFrom] = useState<string>(cloneFromParam);
  const [tickedMasters, setTickedMasters] = useState<Set<string>>(new Set());

  const templatesQ = useQuery({
    queryKey: ['adminBinderTemplates', 'list', { includeInactive: false }],
    queryFn: () => adminBinderTemplates.list({}),
  });
  const existingTemplates = templatesQ.data?.templates ?? [];
  const defaultTpl = existingTemplates.find((t) => t.is_default === 'true');

  useEffect(() => {
    if (mode === 'clone' && !cloneFrom && defaultTpl) {
      setCloneFrom(defaultTpl.template_id);
    }
  }, [mode, cloneFrom, defaultTpl]);

  const createM = useMutation({
    mutationFn: () => {
      const body: Parameters<typeof adminBinderTemplates.create>[0] = {
        name: name.trim(),
        blinding_scope: blindingScope as BinderTemplateBlindingScope,
        description: description.trim() || undefined,
      };
      if (mode === 'checkbox') {
        body.master_folder_template_ids = Array.from(tickedMasters);
      } else if (mode === 'outline') {
        body.outline = outline;
      } else {
        body.clone_from_template_id = cloneFrom;
      }
      return adminBinderTemplates.create(body);
    },
    onSuccess: (res) => {
      if (res.warnings.length > 0) {
        toast.warning(
          `Template created with ${res.warnings.length} warning${res.warnings.length === 1 ? '' : 's'}. Review the editor to see them.`,
        );
      } else {
        toast.success(`Created "${res.template.name}" with ${res.folders.length} folders`);
      }
      qc.invalidateQueries({ queryKey: ['adminBinderTemplates'] });
      navigate(`/admin/binder-templates/${res.template.template_id}`);
    },
    onError: (err: unknown) => {
      toast.error(errorText(err, 'Create failed'));
    },
  });

  function submit(e?: FormEvent) {
    if (e) e.preventDefault();
    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (!SAFE_NAME_RE.test(name.trim())) {
      toast.error('Name cannot contain "/", "\\", or control characters');
      return;
    }
    if (!blindingScope) {
      toast.error('Choose whether this is a blinded or unblinded template');
      return;
    }
    if (mode === 'clone' && !cloneFrom) {
      toast.error('Pick a template to clone from');
      return;
    }
    if (mode === 'outline' && !outline.trim()) {
      toast.error('Paste an indented folder outline');
      return;
    }
    if (mode === 'checkbox' && tickedMasters.size === 0) {
      toast.error('Tick at least one standard folder');
      return;
    }
    createM.mutate();
  }

  return (
    <div className="space-y-6">
      <PageHeader
        backTo="/admin/binder-templates"
        title="New binder template"
        subtitle="Reusable folder layout. Each study picks one at creation and its binder is built from that layout."
        actions={
          <>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => navigate('/admin/binder-templates')}
              disabled={createM.isPending}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={() => submit()}
              disabled={createM.isPending}
            >
              {createM.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
              {createM.isPending ? 'Saving…' : 'Create template'}
            </button>
          </>
        }
      />

      <form onSubmit={submit} className="mx-auto max-w-4xl space-y-6">
        <section className="card p-5 space-y-4">
          <h2 className="text-sm font-semibold text-ink-primary">Basics</h2>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="label">Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={255}
                className="input w-full"
                placeholder="e.g. Sponsor Acme - ISF Index v3"
              />
            </div>
            <div>
              <label className="label">Description (optional)</label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={4000}
                className="input w-full"
                placeholder="Where this layout comes from, when to use it, etc."
              />
            </div>
          </div>
          <fieldset className="space-y-2">
            <legend className="label">Template type *</legend>
            {SCOPE_OPTIONS.map((o) => (
              <label
                key={o.value}
                className="flex cursor-pointer items-start gap-2 rounded border border-surface-border px-3 py-2 text-sm hover:bg-surface-muted/40"
              >
                <input
                  type="radio"
                  name="blinding-scope"
                  value={o.value}
                  checked={blindingScope === o.value}
                  onChange={() => setBlindingScope(o.value)}
                  className="mt-0.5"
                />
                <span className="flex flex-col">
                  <span className="text-ink-primary">{o.label}</span>
                  <span className="text-xs text-ink-muted">{o.hint}</span>
                </span>
              </label>
            ))}
          </fieldset>
        </section>

        <section className="card p-5 space-y-4">
          <div>
            <h2 className="text-sm font-semibold text-ink-primary">
              Folder set
            </h2>
            <p className="text-xs text-ink-muted">
              Choose how the initial folder tree is built. You can edit the tree
              in the editor after the template is created.
            </p>
          </div>

          <div className="grid gap-3 md:grid-cols-3">
            {(Object.keys(MODE_META) as Mode[]).map((m) => (
              <ModeCard
                key={m}
                selected={mode === m}
                title={MODE_META[m].title}
                desc={MODE_META[m].desc}
                icon={MODE_META[m].icon}
                onClick={() => setMode(m)}
              />
            ))}
          </div>

          <div className="border-t border-surface-border pt-5">
            {mode === 'clone' && (
              <CloneModePanel
                templates={existingTemplates}
                selected={cloneFrom}
                onChange={setCloneFrom}
                loading={templatesQ.isLoading}
              />
            )}
            {mode === 'outline' && (
              <OutlineModePanel outline={outline} onChange={setOutline} />
            )}
            {mode === 'checkbox' && (
              <CheckboxModePanel
                tickedMasters={tickedMasters}
                onChange={setTickedMasters}
              />
            )}
          </div>
        </section>

      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Mode picker card
// ---------------------------------------------------------------------------

function ModeCard({
  selected,
  title,
  desc,
  icon: Icon,
  onClick,
}: {
  selected: boolean;
  title: string;
  desc: string;
  icon: typeof Copy;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left rounded-lg border p-4 transition ${
        selected
          ? 'border-accent-500 bg-accent-50 ring-2 ring-accent-200'
          : 'border-surface-border hover:bg-surface-muted/40'
      }`}
      aria-pressed={selected}
    >
      <Icon
        className={`h-5 w-5 mb-2 ${selected ? 'text-accent-600' : 'text-ink-muted'}`}
      />
      <div className="text-sm font-medium text-ink-primary">{title}</div>
      <div className="mt-1 text-xs text-ink-muted">{desc}</div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Mode panels
// ---------------------------------------------------------------------------

function CloneModePanel({
  templates,
  selected,
  onChange,
  loading,
}: {
  templates: Awaited<ReturnType<typeof adminBinderTemplates.list>>['templates'];
  selected: string;
  onChange: (id: string) => void;
  loading: boolean;
}) {
  const defaultTpl = templates.find((t) => t.is_default === 'true');
  return (
    <div className="max-w-xl space-y-2">
      <label className="label">Clone from</label>
      <select
        value={selected}
        onChange={(e) => onChange(e.target.value)}
        className="input w-full"
        disabled={loading}
      >
        {loading && <option value="">Loading templates…</option>}
        {!loading && <option value="">Select a template…</option>}
        {templates.map((t) => (
          <option key={t.template_id} value={t.template_id}>
            {t.is_default === 'true' ? '★ ' : ''}
            {t.name} ({t.reference_number})
          </option>
        ))}
      </select>
      {defaultTpl && !selected && (
        <p className="text-xs text-ink-muted">
          Suggestion: start from the default "{defaultTpl.name}".
        </p>
      )}
    </div>
  );
}

function OutlineModePanel({
  outline,
  onChange,
}: {
  outline: string;
  onChange: (v: string) => void;
}) {
  // Punch-list b13 — a plain <textarea> hands Tab to the browser's default
  // focus-next behavior. Intercept it to indent/outdent the current line
  // instead, matching the tab-indented format the placeholder demonstrates.
  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key !== 'Tab') return;
    e.preventDefault();
    const el = e.currentTarget;
    const { selectionStart, selectionEnd, value } = el;
    const lineStart = value.lastIndexOf('\n', selectionStart - 1) + 1;

    if (e.shiftKey) {
      if (value[lineStart] !== '\t') return;
      const next = value.slice(0, lineStart) + value.slice(lineStart + 1);
      const caret = Math.max(lineStart, selectionStart - 1);
      onChange(next);
      requestAnimationFrame(() => el.setSelectionRange(caret, caret));
      return;
    }

    const next = value.slice(0, selectionStart) + '\t' + value.slice(selectionEnd);
    onChange(next);
    requestAnimationFrame(() => el.setSelectionRange(selectionStart + 1, selectionStart + 1));
  }

  return (
    <div className="space-y-2">
      <label className="label">Folder outline</label>
      <textarea
        value={outline}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        rows={16}
        className="input w-full font-mono text-xs"
        placeholder={`Planning and Procedures
\tProtocol and Amendments
\tInvestigator's Brochure
Regulatory
\tIRB / IEC Approvals`}
      />
      <p className="text-xs text-ink-muted">
        Folders that match a standard folder by name will auto-file uploaded documents.
        The rest stay custom to this template. Tab indents the current line, Shift+Tab outdents.
      </p>
    </div>
  );
}

function CheckboxModePanel({
  tickedMasters,
  onChange,
}: {
  tickedMasters: Set<string>;
  onChange: (next: Set<string>) => void;
}) {
  const [search, setSearch] = useState('');

  const detailQ = useQuery({
    queryKey: ['adminBinderTemplates', 'defaultForPicker'],
    queryFn: async () => {
      const list = await adminBinderTemplates.list({});
      const def = list.templates.find((t) => t.is_default === 'true');
      if (!def) throw new Error('No default template seeded');
      return adminBinderTemplates.get(def.template_id);
    },
    staleTime: 60_000,
  });

  if (detailQ.isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-ink-muted">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading standard folders…
      </div>
    );
  }
  if (detailQ.isError || !detailQ.data) {
    return (
      <div className={`rounded border p-3 text-sm ${errorToneClass(detailQ.error)}`}>
        {errorText(detailQ.error, 'Failed to load standard folders.')}
      </div>
    );
  }

  const allMasters = detailQ.data.masters
    .filter((m) => m.is_active)
    .slice()
    .sort((a, b) => a.artifact_type.localeCompare(b.artifact_type));

  const s = search.trim().toLowerCase();
  const masters = !s
    ? allMasters
    : allMasters.filter(
        (m) =>
          m.artifact_type.toLowerCase().includes(s)
          || (m.zone_name ?? '').toLowerCase().includes(s)
          || (m.section_name ?? '').toLowerCase().includes(s),
      );

  function toggle(id: string) {
    const next = new Set(tickedMasters);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(next);
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <label className="label m-0">Standard folders</label>
        <div className="flex items-center gap-3 text-xs text-ink-muted">
          <span>{tickedMasters.size} of {allMasters.length} ticked</span>
          <button
            type="button"
            className="link"
            onClick={() => onChange(new Set(allMasters.map((m) => m.template_id)))}
          >
            Select all
          </button>
          <button
            type="button"
            className="link"
            onClick={() => onChange(new Set())}
          >
            Clear
          </button>
        </div>
      </div>
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-muted" />
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Filter by folder name, zone, or section…"
          className="input w-full pl-8"
        />
      </div>
      <div className="rounded border border-surface-border">
        {masters.length === 0 ? (
          <div className="px-3 py-6 text-center text-sm text-ink-muted">
            No standard folders match "{search.trim()}".
          </div>
        ) : (
          <ul className="max-h-[440px] overflow-y-auto divide-y divide-surface-border">
            {masters.map((m) => {
              const on = tickedMasters.has(m.template_id);
              return (
                <li key={m.template_id}>
                  <label className="flex cursor-pointer items-start gap-3 px-3 py-2 hover:bg-surface-muted/40">
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={() => toggle(m.template_id)}
                      className="mt-0.5"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-ink-primary">
                        {m.artifact_type}
                      </div>
                      <div className="text-[11px] text-ink-muted">
                        {m.zone_name}
                        {m.section_name ? ` › ${m.section_name}` : ''}
                        {m.inclusion ? ` · ${m.inclusion}` : ''}
                      </div>
                    </div>
                  </label>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
